import fetch from 'isomorphic-fetch'
import hash from 'object-hash'
// import { encode } from '@aytm/app/insights/dashboard/lib/util'
import { asyncTimeout } from '@aytm/app/insights/dashboard/lib/promises'
import { FetchError } from '@aytm/app/insights/dashboard/lib/FetchError'
import type {
  QueryArgumentType,
  FetchStatQueryProps,
  MinimalServerResponseType,
  FetchStatQuery
} from '@aytm/app/insights/dashboard/lib/fetch.types'

const DEFAULT_RETRY_DELAY = 1000

const fetchAbsorptionTimestamps: Record<string, number> = {}

const clientServerQueryHashes = new window.Map<string, string>()

export const fetchStatQuery: FetchStatQuery = async <Q, T>(
  {
    query = [],
    accessKey,
    retries = 60,
    retryDelay = DEFAULT_RETRY_DELAY,
    retryDelayStep = DEFAULT_RETRY_DELAY,
    timestamp,
    queryURL,
    refresh = false,
    sync = false,
    fetchAbsorptionID = null,
    signal = null
  } = {} as FetchStatQueryProps<Q>
) => {
  if (null == queryURL) {
    // no query URL
    return {
      status: 'FAIL',
      errors: [[0, 'no queryURL']]
    }
  }

  const accessKeyCode = accessKey?.code
  if (null == accessKeyCode /* || Date.now() > accessKey?.expire */) {
    // no accessKey
    return {
      status: 'INVALID KEY',
      errors: [[0, 'invalid key']]
    }
  }

  if (!Array.isArray(query) || 0 === query.length) {
    return {
      status: 'EMPTY'
    }
  }

  const clientQueryHash = query && query.length ? hash(query) : null
  const queryHash = clientQueryHash ? clientServerQueryHashes.get(clientQueryHash) : null

  const queryBody: QueryArgumentType<Q> = {
    query,
    key: accessKeyCode,
    refresh,
    sync,
    ...(queryHash && !refresh ? { queryHash } : {})
  }

  if (timestamp) {
    queryBody.timestamp = timestamp
  }

  const currentFetchAbsorptionTimestamp = Date.now()
  if (fetchAbsorptionID) {
    fetchAbsorptionTimestamps[fetchAbsorptionID] = currentFetchAbsorptionTimestamp
  }

  let skipDelay = true
  let retriesLeft = retries
  let currentRetryDelay = retryDelay
  while (0 < retriesLeft) {
    try {
      const response = await fetch(queryURL, {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(queryBody),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(signal ? { signal } : {})
      })
      if (fetchAbsorptionID && currentFetchAbsorptionTimestamp < fetchAbsorptionTimestamps[fetchAbsorptionID]) {
        return { status: 'ABSORBED' }
      }
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      const responseBody = (await response.json()) as MinimalServerResponseType
      if (clientQueryHash && responseBody.queryHash) {
        clientServerQueryHashes.set(clientQueryHash, responseBody.queryHash)
      }

      if (responseBody && 'WAIT' !== responseBody.status) {
        if (clientQueryHash && ('FAIL' === responseBody.status || 'ERROR' === responseBody.status)) {
          clientServerQueryHashes.delete(clientQueryHash)
        }

        return responseBody as unknown as T
      }

      if (skipDelay) {
        skipDelay = false
      } else {
        await asyncTimeout(currentRetryDelay)
      }

      queryBody.timestamp = responseBody.timestamp || queryBody.timestamp
      queryBody.refresh = false
      queryBody.queryHash = responseBody.queryHash
      queryBody.query = []
      currentRetryDelay += retryDelayStep
      retriesLeft--
    } catch {
      await asyncTimeout(currentRetryDelay)
      currentRetryDelay += retryDelayStep
    }

    retriesLeft--
  }
  return {
    status: 'FAIL',
    errors: [[0, 'fetch failed']]
  }
}

/**
 * All error conditions within are thrown as FetchError, all response case errors (except wait) are also thrown
 */
export const executeRequestToQueryAPI: FetchStatQuery = async <Q, T>(
  {
    queryURL = null,
    accessKey = null,
    query = [],
    queryHash = null,
    timestamp = null,
    refresh = false,
    sync = false
  } = {} as FetchStatQueryProps<Q>
) => {
  // Get access key code
  const accessKeyCode = accessKey?.code
  // debugger
  // Check query URL
  if (null == queryURL) {
    throw new FetchError('No queryURL', 'NO_QUERY_URL')
  }

  // Check access key
  if (null == accessKeyCode) {
    // no accessKey
    throw new FetchError('No access key', 'NO_ACCESS_KEY')
  }

  // Check for query or queryHash
  if ((!Array.isArray(query) || 0 === query.length) && !queryHash) {
    throw new FetchError('Empty query', 'EMPTY_QUERY')
  }

  // Get query body
  const queryBody: QueryArgumentType<Q> = queryHash
    ? {
        key: accessKeyCode,
        query: [],
        queryHash,
        refresh,
        sync
      }
    : {
        key: accessKeyCode,
        query,
        refresh,
        sync
      }

  // Append timestamp
  if (timestamp) {
    queryBody.timestamp = timestamp
  }

  try {
    // Run request
    const response = await fetch(queryURL, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(queryBody),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    })

    // Check response
    if (!response.ok) {
      throw new FetchError(response.statusText || 'fetch failed', 'FETCH_FAILED')
    }

    // Get response body
    const responseBody = (await response.json()) as MinimalServerResponseType

    if (!responseBody || !responseBody.status || !['ABSORBED', 'OK', 'WAIT'].includes(responseBody.status)) {
      if ('INVALID KEY' === responseBody.status) {
        throw new FetchError('Invalid access key', 'INVALID_KEY', responseBody?.status)
      }
      throw new FetchError('Invalid response', 'INVALID_RESPONSE', responseBody?.status)
    }

    // Return query result
    return responseBody as unknown as T
  } catch (error: unknown) {
    if (FetchError.isFetchError(error)) {
      throw error
    }
    throw new FetchError((error as Error).message || 'Fetch failed', 'FETCH_FAILED', null, error as Error)
  }
}
